import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { sendProjectChat } from '../api/chat';
import { createComponent, updateComponent } from '../api/ideas';
import { FaPaperPlane, FaSpinner, FaChevronUp, FaChevronDown, FaRobot, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';

// Thinking animation component
const ThinkingAnimation = () => {
  const [dots, setDots] = useState('');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '...') return '';
        return prev + '.';
      });
    }, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="flex items-center space-x-2">
      <span className="text-[var(--text-secondary)] text-sm">
        Thinking{dots}
      </span>
    </div>
  );
};

// Interactive action message component for requesting component actions
const InteractiveActionMessage = ({ action, componentType, componentName, onConfirm, onDismiss }) => {
  const isAdd = action === 'add';
  const Icon = isAdd ? FaPlus : FaEdit;
  const actionText = isAdd ? 'Add' : 'Edit';
  
  return (
    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg animate-scale-in">
      <div className="flex items-start space-x-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Icon className="text-blue-600" size={16} />
        </div>
        <div className="flex-1">
          <p className="text-blue-900 font-medium text-sm mb-2">
            Would you like me to {actionText.toLowerCase()} the {componentType}: "{componentName}"?
          </p>
          <div className="flex space-x-2">
            <button
              onClick={onConfirm}
              className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-all duration-200 transform hover:scale-105 active:scale-95 button-magical"
            >
              Yes
            </button>
            <button
              onClick={onDismiss}
              className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded-md hover:bg-gray-400 transition-all duration-200 transform hover:scale-105 active:scale-95"
            >
              No
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Completed action message component for confirmed actions
const CompletedActionMessage = ({ action, componentType, componentName }) => {
  const isAdd = action === 'add';
  const Icon = isAdd ? FaPlus : FaEdit;
  const actionText = isAdd ? 'Added' : 'Edited';
  const bgColor = isAdd ? 'bg-success-light' : 'bg-blue-50';
  const textColor = isAdd ? 'text-success' : 'text-blue-800';
  const iconColor = isAdd ? 'text-success' : 'text-blue-600';
  
  return (
    <div className={`${bgColor} ${textColor} p-3 rounded-lg italic`}>
      <div className="flex items-center space-x-2">
        <Icon className={`${iconColor}`} size={12} />
        <span className="text-sm">
          {actionText} {componentType}: {componentName}
        </span>
      </div>
    </div>
  );
};

const ChatIntegrated = forwardRef(({ ideaId, components, setComponents, refreshSidebar, mode = 'integrated', hideInput = false }, ref) => {
  // Generate unique storage keys for this idea's chat data
  const chatStorageKey = `chat_messages_${ideaId}`;
  const actionsStorageKey = `chat_actions_${ideaId}`;
  
  // Load messages from localStorage on mount
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(chatStorageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Failed to load chat messages from localStorage:', error);
      return [];
    }
  });
  
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Load action states from localStorage on mount
  const [pendingActions, setPendingActions] = useState(() => {
    try {
      const saved = localStorage.getItem(actionsStorageKey);
      const data = saved ? JSON.parse(saved) : {};
      return data.pendingActions || [];
    } catch (error) {
      console.warn('Failed to load chat actions from localStorage:', error);
      return [];
    }
  });
  
  const [expandedActions, setExpandedActions] = useState(() => {
    try {
      const saved = localStorage.getItem(actionsStorageKey);
      const data = saved ? JSON.parse(saved) : {};
      return data.expandedActions || {};
    } catch (error) {
      console.warn('Failed to load expanded actions from localStorage:', error);
      return {};
    }
  });
  
  const [resolvedActions, setResolvedActions] = useState(() => {
    try {
      const saved = localStorage.getItem(actionsStorageKey);
      const data = saved ? JSON.parse(saved) : {};
      return data.resolvedActions || {};
    } catch (error) {
      console.warn('Failed to load resolved actions from localStorage:', error);
      return {};
    }
  });
  
  const [showFullError, setShowFullError] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    try {
      localStorage.setItem(chatStorageKey, JSON.stringify(messages));
      
      // Dispatch a custom event to sync across chat instances
      window.dispatchEvent(new CustomEvent('chatMessagesUpdated', {
        detail: { ideaId, messages }
      }));
    } catch (error) {
      console.warn('Failed to save chat messages to localStorage:', error);
    }
  }, [messages, chatStorageKey, ideaId]);

  // Save action states to localStorage whenever they change
  useEffect(() => {
    try {
      const actionsData = {
        pendingActions,
        expandedActions,
        resolvedActions
      };
      localStorage.setItem(actionsStorageKey, JSON.stringify(actionsData));
    } catch (error) {
      console.warn('Failed to save chat actions to localStorage:', error);
    }
  }, [pendingActions, expandedActions, resolvedActions, actionsStorageKey]);

  // Listen for chat updates from other instances
  useEffect(() => {
    const handleChatUpdate = (event) => {
      if (event.detail.ideaId === ideaId) {
        // Only update if the messages are actually different to prevent loops
        const currentMessagesStr = JSON.stringify(messages);
        const newMessagesStr = JSON.stringify(event.detail.messages);
        
        if (currentMessagesStr !== newMessagesStr) {
          setMessages(event.detail.messages);
        }
      }
    };

    window.addEventListener('chatMessagesUpdated', handleChatUpdate);
    return () => window.removeEventListener('chatMessagesUpdated', handleChatUpdate);
  }, [ideaId, mode, messages]);

  // Error display component with collapsible functionality
  const ErrorDisplay = ({ error, showFullError, setShowFullError }) => {
    if (!error) return null;
    
    const lines = error.split('\n');
    const shouldTruncate = lines.length > 2 || error.length > 150;
    const displayText = shouldTruncate && !showFullError 
      ? lines.slice(0, 2).join('\n') + (lines.length > 2 ? '...' : '')
      : error;
    
    return (
      <div className="text-sm" style={{ color: 'var(--danger)' }}>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {displayText}
        </div>
        {shouldTruncate && (
          <button
            onClick={() => setShowFullError(!showFullError)}
            className="text-xs underline opacity-75 hover:opacity-100 mt-1"
            style={{ color: 'var(--danger)' }}
          >
            {showFullError ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    );
  };

  // Different modes: 'integrated' (desktop right column), 'mobile' (bottom bar)
  const isMobile = mode === 'mobile';

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Scroll to bottom when minimized state changes
    scrollToBottom();
  }, [isMinimized]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => {
    return {
      sendMessage: (messageText) => {
        handleSendMessageWithText(messageText);
      }
    };
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear all chat messages? This action cannot be undone.')) {
      setMessages([]);
      setPendingActions([]);
      setExpandedActions({});
      setResolvedActions({});
      setError('');
      
      // Clear from localStorage
      try {
        localStorage.removeItem(chatStorageKey);
        localStorage.removeItem(actionsStorageKey);
        
        // Dispatch event to sync clearing across instances
        window.dispatchEvent(new CustomEvent('chatMessagesUpdated', {
          detail: { ideaId, messages: [] }
        }));
      } catch (error) {
        console.warn('Failed to clear chat messages from localStorage:', error);
      }
    }
  };

  const handleSendMessageWithText = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = messageText.trim();
    setIsLoading(true);
    setError('');

    // Add user message immediately - using same format as desktop
    const newUserMessage = {
      id: Date.now(),
      text: userMessage,
      sender: 'user',
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Please log in to send messages');
        setIsLoading(false);
        return;
      }

      // Transform chat history to expected format - same as desktop
      const chatHistory = messages
        .filter(msg => msg.sender === 'user' || msg.sender === 'ai')
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));

      const response = await sendProjectChat(ideaId, userMessage, token, chatHistory);
      
      let newMessages = [...messages, newUserMessage];
      let newPendingActions = [...pendingActions];

      // Handle response - same logic as desktop
      if (Array.isArray(response)) {
        response.forEach((item) => {
          if (item.message) {
            const botMessage = {
              id: Date.now() + Math.random(),
              text: item.message,
              sender: 'ai',
              timestamp: Date.now()
            };
            newMessages.push(botMessage);
          }
          
          // Handle add component actions
          if (item.addcomponent) {
            const actionId = uuidv4();
            const action = {
              id: actionId,
              type: 'add',
              component: item.addcomponent,
              message: `Would you like me to add a new ${item.addcomponent.type} component titled "${item.addcomponent.title}"?`,
              resolvedMessage: `Added a new ${item.addcomponent.type} component titled "${item.addcomponent.title}"`,
              details: JSON.stringify(item.addcomponent.data, null, 2)
            };
            newPendingActions.push(action);
            newMessages.push({
              text: action.message,
              sender: 'action',
              actionId: actionId,
              action: action,
              id: actionId
            });
          }
          
          // Handle edit component actions
          if (item.editcomponent) {
            const existingComp = components.find(c => c.id === item.editcomponent.id);
            const actionId = uuidv4();
            const action = {
              id: actionId,
              type: 'edit',
              component: item.editcomponent,
              message: `Would you like to update the ${existingComp?.type || 'component'} titled "${existingComp?.title || ''}"?`,
              resolvedMessage: `Updated the ${existingComp?.type || 'component'} titled "${existingComp?.title || ''}"`,
              details: `Changes:\n${JSON.stringify(item.editcomponent.data, null, 2)}`
            };
            newPendingActions.push(action);
            newMessages.push({
              text: action.message,
              sender: 'action',
              actionId: actionId,
              action: action,
              id: actionId
            });
          }
        });
      } else if (response.message) {
        const botMessage = {
          id: Date.now() + 1,
          text: response.message,
          sender: 'ai',
          timestamp: Date.now()
        };
        newMessages.push(botMessage);
      }

      setMessages(newMessages);
      setPendingActions(newPendingActions);

      if (response.components_updated) {
        if (setComponents) {
          setComponents(response.components_updated);
        }
        if (refreshSidebar) {
          refreshSidebar();
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMessage = 'Failed to send message';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = 'Failed to send message: ' + error.response.data.detail.map(e => e.msg || e).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = 'Failed to send message: ' + error.response.data.detail;
        } else {
          errorMessage = 'Failed to send message: ' + JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMessage = 'Failed to send message: ' + error.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    setIsLoading(true);
    setError('');

    // Add user message immediately
    const newUserMessage = {
      id: Date.now(),
      text: userMessage,
      sender: 'user',
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Please log in to send messages');
        setIsLoading(false);
        return;
      }

      // Transform chat history to expected format
      const chatHistory = messages
        .filter(msg => msg.sender === 'user' || msg.sender === 'ai')
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));

      const response = await sendProjectChat(ideaId, userMessage, token, chatHistory);
      
      let newMessages = [...messages, newUserMessage];
      let newPendingActions = [...pendingActions];

      // Handle response - it should be an array
      if (Array.isArray(response)) {
        response.forEach((item) => {
          if (item.message) {
            const botMessage = {
              id: Date.now() + Math.random(),
              text: item.message,
              sender: 'ai',
              timestamp: Date.now()
            };
            newMessages.push(botMessage);
          }
          
          // Handle add component actions
          if (item.addcomponent) {
            const actionId = uuidv4();
            const action = {
              id: actionId,
              type: 'add',
              component: item.addcomponent,
              message: `Would you like me to add a new ${item.addcomponent.type} component titled "${item.addcomponent.title}"?`,
              resolvedMessage: `Added a new ${item.addcomponent.type} component titled "${item.addcomponent.title}"`,
              details: JSON.stringify(item.addcomponent.data, null, 2)
            };
            newPendingActions.push(action);
            newMessages.push({
              text: action.message,
              sender: 'action',
              actionId: actionId,
              action: action,
              id: actionId
            });
          }
          
          // Handle edit component actions
          if (item.editcomponent) {
            const existingComp = components.find(c => c.id === item.editcomponent.id);
            const actionId = uuidv4();
            const action = {
              id: actionId,
              type: 'edit',
              component: item.editcomponent,
              message: `Would you like to update the ${existingComp?.type || 'component'} titled "${existingComp?.title || ''}"?`,
              resolvedMessage: `Updated the ${existingComp?.type || 'component'} titled "${existingComp?.title || ''}"`,
              details: `Changes:\n${JSON.stringify(item.editcomponent.data, null, 2)}`
            };
            newPendingActions.push(action);
            newMessages.push({
              text: action.message,
              sender: 'action',
              actionId: actionId,
              action: action,
              id: actionId
            });
          }
        });
      } else if (response.message) {
        const botMessage = {
          id: Date.now() + 1,
          text: response.message,
          sender: 'ai',
          timestamp: Date.now()
        };
        newMessages.push(botMessage);
      }

      setMessages(newMessages);
      setPendingActions(newPendingActions);

      if (response.components_updated) {
        if (setComponents) {
          setComponents(response.components_updated);
        }
        if (refreshSidebar) {
          refreshSidebar();
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      let errorMessage = 'Failed to send message';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = 'Failed to send message: ' + err.response.data.detail.map(e => e.msg || e).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = 'Failed to send message: ' + err.response.data.detail;
        } else {
          errorMessage = 'Failed to send message: ' + JSON.stringify(err.response.data.detail);
        }
      } else if (err.message) {
        errorMessage = 'Failed to send message: ' + err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle confirming an action
  const handleConfirmAction = (messageId, action, componentType, componentName) => {
    // Remove the interactive message and add a completed action message
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        return {
          ...msg,
          sender: 'completed_action',
          pending: false
        };
      }
      return msg;
    }));
    
    // Here you would normally trigger the actual component creation/editing
    // For now, we'll just show the confirmation message
  };

  // Handle dismissing an action
  const handleDismissAction = (messageId) => {
    // Remove the interactive message
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  const toggleActionDetails = (actionId) => {
    setExpandedActions(prev => ({
      ...prev,
      [actionId]: !prev[actionId]
    }));
  };

  const handleAction = async (action, approved) => {
    setResolvedActions(prev => ({
      ...prev,
      [action.id]: { approved, italic: true }
    }));

    if (!approved) {
      setPendingActions(prev => prev.filter(a => a.id !== action.id));
      // Update the message to show it was dismissed
      setMessages(prev => prev.map(msg => {
        if (msg.actionId === action.id) {
          return {
            ...msg,
            text: `Action dismissed: ${action.message.replace('Would you like me to', 'Did not')}`,
            dismissed: true
          };
        }
        return msg;
      }));
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      if (action.type === 'add') {
        await createComponent(ideaId, action.component, token);
      } 
      else if (action.type === 'edit') {
        await updateComponent(
          action.component.id, 
          {
            title: action.component.title,
            data: action.component.data
          }, 
          token
        );
      }

      // Update the message text to show the resolved state
      setMessages(prev => prev.map(msg => {
        if (msg.actionId === action.id) {
          return {
            ...msg,
            text: action.resolvedMessage
          };
        }
        return msg;
      }));

      // Refresh components
      if (setComponents && refreshSidebar) {
        const { getIdeaComponents } = await import('../api/ideas');
        const updatedComponents = await getIdeaComponents(ideaId, token);
        setComponents(updatedComponents);
        refreshSidebar();
      }

      // Remove the completed action from pending
      setPendingActions(prev => prev.filter(a => a.id !== action.id));
    } catch (error) {
      console.error('Error handling action:', error);
      setMessages(prev => [
        ...prev,
        { text: `Error: ${error.message}`, sender: 'ai', id: Date.now() }
      ]);
      // Revert resolved state on error
      setResolvedActions(prev => {
        const newState = { ...prev };
        delete newState[action.id];
        return newState;
      });
    }
  };

  // Render message based on type
  const renderMessage = (msg) => {
    if (msg.sender === 'action') {
      return (
        <div
          key={`msg-${msg.id}`}
          className={`p-3 rounded-lg text-sm max-w-[85%] self-start ${
            resolvedActions[msg.actionId]?.italic
              ? 'bg-[var(--bg-hover)] text-[var(--text-primary)] italic'
              : 'bg-[var(--bg-hover)] text-[var(--text-primary)] border-2 border-[var(--primary)]'
          }`}
        >
          <div className="flex justify-between items-start">
            <p>{msg.text}</p>
            {!resolvedActions[msg.actionId] && (
              <button 
                onClick={() => toggleActionDetails(msg.actionId)}
                className="ml-2 text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
              >
                {expandedActions[msg.actionId] ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            )}
          </div>
          
          {!resolvedActions[msg.actionId] && expandedActions[msg.actionId] && (
            <div className="mt-2 p-2 bg-[var(--bg-primary)] rounded text-xs overflow-x-auto">
              <pre className="whitespace-pre-wrap">
                {pendingActions.find(a => a.id === msg.actionId)?.details}
              </pre>
            </div>
          )}
          
          {!resolvedActions[msg.actionId] && (
            <div className="flex gap-2 mt-2">
              <button
                className="px-8 py-1.5 rounded bg-[var(--primary)] text-white hover:bg-[var(--primary-dark)] transition-colors text-xs"
                onClick={() => handleAction(msg.action, true)}
              >
                Yes
              </button>
              <button
                className="px-8 py-1.5 rounded border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--text-secondary)] hover:text-white transition-colors text-xs"
                onClick={() => handleAction(msg.action, false)}
              >
                No
              </button>
            </div>
          )}
        </div>
      );
    }
    
    // Debug logging for AI messages
    if (msg.sender === 'ai' && process.env.NODE_ENV === 'development') {
      console.log('Rendering AI message:', { id: msg.id, text: msg.text, textLength: msg.text?.length });
    }
    
    // Get the message text with fallback
    const messageText = msg.text || msg.content || '[Empty message]';
    
    return (
      <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[85%] p-3 rounded-lg ${
          msg.sender === 'user' 
            ? 'bg-[var(--primary)] text-white' 
            : 'bg-[var(--bg-hover,#f1f5f9)] text-[var(--text-primary,#1f2937)] border border-[var(--border-color,#e5e7eb)]'
        }`}>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{messageText}</p>
        </div>
      </div>
    );
  };

  // Mobile bottom bar mode - but not when hideInput is true (embedded in parent toolbar)
  if (isMobile && !hideInput) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-[var(--bg-secondary)] z-50 shadow-lg" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Expandable chat area */}
        {isExpanded && (
          <div className="max-h-[70vh] min-h-[70vh] overflow-hidden bg-[var(--bg-secondary)] backdrop-blur-xl" style={{ height: '70vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[var(--primary)] bg-opacity-10 rounded-lg">
                  <FaRobot size={16} className="text-[var(--primary)]" />
                </div>
                <h3 className="font-semibold text-[var(--text-primary)]">Project Chat</h3>
              </div>
              <div className="flex items-center space-x-2">
                {messages.length > 0 && (
                  <button
                    onClick={handleClearChat}
                    className="p-2 hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200 text-[var(--text-secondary)] hover:text-red-500"
                    title="Clear chat"
                  >
                    <FaTrash size={14} />
                  </button>
                )}
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  <FaChevronDown size={18} className="transform transition-transform duration-200" />
                </button>
              </div>
            </div>
            
            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[var(--bg-secondary)] min-h-0 max-h-[calc(70vh-160px)] scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-transparent">
              {messages.length === 0 ? (
                <div className="text-center text-[var(--text-secondary)] py-12">
                  <div className="p-4 bg-[var(--primary)] bg-opacity-10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <FaRobot className="text-3xl text-[var(--primary)]" />
                  </div>
                  <p className="text-lg font-medium mb-2 text-[var(--text-primary)]">Start a conversation!</p>
                  <p className="text-sm mb-6">Ask questions about your idea, request new components, or get suggestions for changes.</p>
                </div>
              ) : (
                messages.map(renderMessage)
              )}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-[var(--bg-hover)] p-3 rounded-lg w-32">
                    <div className="flex items-center space-x-2">
                      <FaRobot className="text-[var(--primary)]" />
                      <ThinkingAnimation />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        {/* Chat input bar */}
        <div className="p-4 bg-[var(--bg-secondary)]" style={{ paddingBottom: `calc(16px + env(safe-area-inset-bottom))` }}>
          {/* Token usage warning above text box when no messages and expanded */}
          {messages.length === 0 && isExpanded && (
            <div className="text-center mb-3">
              <p className="text-xs text-[var(--text-secondary)] bg-[var(--bg-primary)] px-3 py-2 rounded-lg inline-block border border-[var(--border)]">
                This chat uses more AI tokens - be mindful of usage
              </p>
            </div>
          )}
          
          <form onSubmit={handleSendMessage} className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => {
                  // Hide URL bar on mobile when chat opens and handle keyboard
                  if (window.innerHeight < window.innerWidth) return; // Skip on landscape
                  
                  // Expand chat first
                  setIsExpanded(true);
                  
                  // Handle viewport for mobile browsers with bottom URL bars
                  setTimeout(() => {
                    // Force viewport to account for mobile browser UI
                    const vh = window.innerHeight * 0.01;
                    document.documentElement.style.setProperty('--vh', `${vh}px`);
                    
                    // Try to hide address bar
                    window.scrollTo(0, 1);
                    
                    // Additional scroll after keyboard appears
                    setTimeout(() => {
                      window.scrollTo(0, document.body.scrollHeight);
                    }, 300);
                  }, 100);
                }}
                onBlur={() => {
                  // Reset viewport when keyboard closes
                  setTimeout(() => {
                    const vh = window.innerHeight * 0.01;
                    document.documentElement.style.setProperty('--vh', `${vh}px`);
                  }, 300);
                }}
                placeholder="Ask about your idea..."
                className="w-full p-4 pr-14 bg-[var(--bg-primary)] rounded-xl text-[var(--text-primary)] placeholder-[var(--text-secondary)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent shadow-sm"
                rows="1"
                style={{ minHeight: '56px' }}
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!message.trim() || isLoading}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-3 text-white bg-[var(--primary)] hover:bg-[var(--primary-dark)] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {isLoading ? <FaSpinner className="animate-spin" size={16} /> : <FaPaperPlane size={16} />}
              </button>
            </div>
          </form>
          
          {error && (
            <div className="mt-3 px-2">
              <ErrorDisplay error={error} showFullError={showFullError} setShowFullError={setShowFullError} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mobile mode with hidden input (embedded in parent toolbar)
  if (isMobile && hideInput) {
    return (
      <div className="flex flex-col h-full bg-[var(--bg-secondary)]">
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-transparent">
          {messages.length === 0 ? (
            <div className="text-center text-[var(--text-secondary)] py-12">
              <div className="p-4 bg-[var(--primary)] bg-opacity-10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FaRobot className="text-2xl text-[var(--primary)]" />
              </div>
              <p className="text-base font-medium mb-2 text-[var(--text-primary)]">Chat with AI Assistant</p>
              <p className="text-sm">Ask questions about your idea or request new components.</p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div key={msg.id}>
                {renderMessage(msg)}
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-[var(--bg-hover)] p-3 rounded-lg max-w-xs">
                <div className="flex items-center space-x-2">
                  <FaRobot className="text-[var(--primary)]" />
                  <ThinkingAnimation />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {error && (
          <div className="px-4 pb-2">
            <ErrorDisplay error={error} showFullError={showFullError} setShowFullError={setShowFullError} />
          </div>
        )}
      </div>
    );
  }

  // Integrated mode (desktop right column)
  return (
    <div className={`flex flex-col bg-[var(--bg-secondary)] rounded-lg shadow-sm transition-all duration-200 ease-out w-full max-w-full ${
      isMinimized ? 'h-3/5' : 'h-full'
    } min-h-0`}>
      {/* Chat header */}
      <div className="flex items-center justify-between bg-[var(--bg-secondary)] rounded-t-lg p-4 border-b border-[var(--border-color)]">
        <div className="flex items-center space-x-3">
          {!isMinimized && (
            <div className="p-2 bg-[var(--bg-secondary)] rounded-lg">
              <FaRobot size={16} className="text-[var(--primary)]" />
            </div>
          )}
          <h3 className={`font-semibold text-[var(--text-primary)] ${isMinimized ? 'text-sm' : ''}`}>Project Chat</h3>
        </div>
        <div className="flex items-center space-x-2">
          {messages.length > 0 && (
            <button
              onClick={handleClearChat}
              className="p-2 hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200 text-[var(--text-secondary)] hover:text-red-500 hover:scale-110"
              title="Clear chat"
            >
              <FaTrash size={14} />
            </button>
          )}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-2 hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:scale-110"
          >
            {isMinimized ? (
              <FaChevronUp size={16} className="transform transition-transform duration-300 hover:scale-110" />
            ) : (
              <FaChevronDown size={16} className="transform transition-transform duration-300 hover:scale-110" />
            )}
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div className={`flex-1 overflow-y-auto space-y-2 bg-[var(--bg-secondary)] min-h-0 p-4 scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-transparent `}>
        {messages.length === 0 ? (
          <div className={`text-center text-[var(--text-secondary)] ${isMinimized ? 'py-16' : 'py-8'}`}>
            <div className={`p-4 bg-[var(--primary)] bg-opacity-10 rounded-full ${isMinimized ? 'w-8 h-8' : 'w-16 h-16'} flex items-center justify-center mx-auto mb-4`}>
              <FaRobot className={`${isMinimized ? 'text-lg' : 'text-2xl'} text-[var(--primary)]`} />
            </div>
            <p className={`${isMinimized ? 'text-sm' : 'text-lg'} font-medium mb-2 text-[var(--text-primary)]`}>Project AI Assistant</p>
            {!isMinimized && <p className="text-sm mb-6">Ask questions about your idea, request new components, or get suggestions!</p>}
          </div>
        ) : (
          // Always show actual messages, just in a shorter container when minimized
          messages.map((msg, index) => (
            <div key={msg.id}>
              {renderMessage(msg)}
            </div>
          ))
        )}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-[var(--bg-hover)] p-3 rounded-lg w-32">
              <div className="flex items-center space-x-2">
                <FaRobot className="text-[var(--primary)]" />
                <ThinkingAnimation />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input area - always present unless hideInput is true */}
      {!hideInput && (
        <div className="bg-[var(--bg-secondary)] rounded-b-lg p-4">
          {/* Token usage warning above text box when no messages */}
          {messages.length === 0 && (
            <div className="text-center mb-3">
              <p className="text-xs text-[var(--text-secondary)] bg-[var(--bg-primary)] px-3 py-2 rounded-lg inline-block">
                This chat uses more AI tokens - be mindful of usage
              </p>
            </div>
          )}
          
          <form onSubmit={handleSendMessage} className="space-y-2">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your idea..."
                className="w-full p-3 pr-12 bg-[var(--bg-primary)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-secondary)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                rows="1"
                style={{ minHeight: '44px' }}
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!message.trim() || isLoading}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-2"
              >
                {isLoading ? <FaSpinner className="animate-spin" size={16} /> : <FaPaperPlane size={16} />}
              </button>
            </div>
            
            {error && (
              <div className="mt-2">
                <ErrorDisplay error={error} showFullError={showFullError} setShowFullError={setShowFullError} />
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
});

export default ChatIntegrated;
